import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges } from '@angular/core';
import { AbstractControl, UntypedFormControl, UntypedFormGroup, ValidationErrors, ValidatorFn, Validators } from '@angular/forms';
import { INSAutoCompleteOption } from '@norfolk-southern/accessns-components';
import { Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged, takeUntil } from 'rxjs/operators';

import { NSCommonConstants } from '@ruby/configs/common.constants';
import { IShortLineComment, IShortLineContact, IShortLineProfile } from '@ruby/modules/short-line-profile/models/short-line-profile.interface';
import { EShortLineCommentType, EShortLineProfile } from '@ruby/shared/enums/short-line-profile.enum';
import { IStation } from '@ruby/shared/models/commons/station.interface';
import { UtilsService } from '@ruby/shared/services/utils/utils.service';

@Component({
  selector: 'ng-commercial-summary',
  templateUrl: './commercial-summary.component.html',
  styleUrls: ['./commercial-summary.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class CommercialSummaryComponent implements OnInit, OnChanges, OnDestroy {
  @Input() profile?: IShortLineProfile;
  @Input() loading!: boolean;
  @Input() isNew!: boolean;
  @Input() canEdit!: boolean;
  @Input() isAdmin!: boolean;
  @Input() loadingCities!: boolean;
  @Input() isError!: boolean;
  @Input() loadingSearch!: boolean;
  @Input() states!: Array<INSAutoCompleteOption>;
  @Input() cities!: Array<INSAutoCompleteOption>;
  @Output() loadOriginStationsData = new EventEmitter<IStation>();
  @Output() saveProfile = new EventEmitter<IShortLineProfile>();
  @Output() createProfile = new EventEmitter<IShortLineProfile>();
  @Output() searchReportingMark = new EventEmitter<string>();
  summary = new UntypedFormGroup({
    reportingMark: new UntypedFormControl('', [Validators.required, this.reportingMarkValidator().bind(this)]),
    roadName: new UntypedFormControl(undefined),
    primaryContact: new UntypedFormControl(),
    title: new UntypedFormControl(),
    phone: new UntypedFormControl(undefined, this.phoneLengthValidator()),
    fax: new UntypedFormControl(undefined, this.phoneLengthValidator()),
    email: new UntypedFormControl(undefined, Validators.email),
    webAddress: new UntypedFormControl(undefined, [Validators.required, Validators.pattern(NSCommonConstants.hyperLinkPattern)]),
    addressLine: new UntypedFormControl(),
    addressCity: new UntypedFormControl('', this.autocompleteValidator()),
    addressState: new UntypedFormControl('', this.autocompleteValidator()),
    addressZip: new UntypedFormControl(undefined),
    companyEid: new UntypedFormControl(),
    customerServicePhone: new UntypedFormControl('', [Validators.required, this.phoneLengthValidator()]),
    customerServiceHours: new UntypedFormControl('', Validators.required),
    salesRepresentative: new UntypedFormControl({ value: 'None Designated', disabled: true }),
    history: new UntypedFormControl(),
    equipmentInfo: new UntypedFormControl(),
    paperBarriers: new UntypedFormControl(),
    nsComments: new UntypedFormControl(),
    nsManager: new UntypedFormControl('', Validators.required),
    accountingStatus: new UntypedFormControl('', Validators.required),
    includeArea: new UntypedFormControl('', Validators.required),
    salesOffice: new UntypedFormControl('', Validators.required),
    accountComments: new UntypedFormControl(),
    fakeAccountingStatus: new UntypedFormControl('', Validators.required)
  });
  isEdit = false;
  debounceTimeout = 200;
  pattern = NSCommonConstants.charsOnlyAndLimited;
  abbrPattern = /^\w{1,50}$/;
  private subscriptionNotifier = new Subject<void>();

  constructor(private utilsSvc: UtilsService) { }

  ngOnInit(): void {
    this.setup();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.isNew) {
      this.reset();
      changes.isNew.currentValue ? this.summary.enable() : this.summary.disable();
      this.disableUnusedFields();
      this.summary.controls.roadName.setValidators(Validators.required);
    }

    if (changes.profile && changes.profile.currentValue && !this.isNew) {
      this.patchValues(changes.profile.currentValue);
    }

    if (changes.profile && !changes.profile.currentValue && changes.profile.previousValue && !this.isNew) {
      this.reset();
    }

    if (changes.isError) {
      this.summary.controls.reportingMark.markAsDirty();
      this.summary.controls.reportingMark.markAsTouched();
      this.summary.controls.reportingMark.updateValueAndValidity();
    }
  }

  ngOnDestroy(): void {
    this.subscriptionNotifier.next();
    this.subscriptionNotifier.complete();
  }

  /**
   * Edit
   *
   * @summary Method called when edit button is clicked
   * @returns void
   */
  edit(): void {
    this.isEdit = true;
    this.summary.enable();
    this.summary.controls.reportingMark.disable();
    this.summary.markAllAsTouched();
    this.disableUnusedFields();
  }

  /**
   * Cancel
   *
   * @summary Method called when cancel button is clicked
   * @returns void
   */
  cancel(): void {
    this.isEdit = false;
    this.summary.disable();
    this.summary.controls.primaryContact.clearValidators();
    if (this.profile) {
      this.summary.reset();
      this.patchValues(this.profile);
    }
  }

  /**
   * Autocomplete Selected
   *
   * @summary Method called when autocomplete is selected
   * @param formCtrlName: string
   * @param previousCtrlName: string
   * @returns void
   */
  autocompleteSelected(formCtrlName: string, previousCtrlName: string): void {
    if (typeof this.summary.get(previousCtrlName)?.value !== 'string') {
      this.summary.get(formCtrlName)?.enable();
    }
  }

  /**
   * Phone length validator
   *
   * @summary return a validator to validate alternative phone and length
   * @returns ValidatorFn
   */
  phoneLengthValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors => {
      const value = this.clearPhoneNumber(control.value);
      return value === undefined || value.length === 10 ? {} : { invalidPhone: true };
    };
  }

  /**
   * Clear phone number
   *
   * @summary remove all special characters from the phone number leaving only the digits
   * @param phone?: string
   * @returns string | undefined
   */
  clearPhoneNumber(phone?: string): string | undefined {
    let value = phone || '';
    value = value.replace(/\(|\)|-|\s/g, '');
    return value ? value : undefined;
  }

  /**
   * Autocomplete Validator
   *
   * @summary Autocomplete Validator
   * @param controlName?: string
   * @returns ValidatorFn
   */
  autocompleteValidator(controlName?: string): ValidatorFn {
    return (control: AbstractControl): ValidationErrors => {
      let result = {};
      if (typeof control.value === 'string' && control.value !== '') {
        result = { invalidSelection: { value: control.value } };
      }
      return result;
    };
  }

  /**
   * Save
   *
   * @summary This method is called when save button is clicked
   * @returns void
   */
  save(): void {
    const extractedData = this.utilsSvc.extractValuesFromObject(this.summary.value);
    const profile: IShortLineProfile = {
      accountingStatus: extractedData.accountingStatus,
      comments: this.getComments(),
      contacts: this.getContacts(),
      companyEid: extractedData.companyEid,
      customerServiceHours: extractedData.customerServiceHours,
      customerServicePhone: extractedData.customerServicePhone ? extractedData.customerServicePhone.replace(new RegExp('\\(|\\)|\-', 'g'), '') : undefined,
      fakeAccountingStatus: extractedData.fakeAccountingStatus,
      includeArea: extractedData.includeArea,
      nsManager: extractedData.nsManager,
      reportingMark: this.summary.controls.reportingMark.value.toUpperCase(),
      roadName: extractedData.roadName,
      salesOffice: extractedData.salesOffice,
      webAddress: extractedData.webAddress
    };
    if (this.isNew) {
      this.createProfile.emit(profile);
    } else {
      this.saveProfile.emit(profile);
    }
    this.isEdit = false;
    this.summary.disable();
  }

  /**
   * Reporting Mark validator
   *
   * @summary return a validator to validate reporting mark if it is duplicated
   * @returns ValidatorFn
   */
  reportingMarkValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors => this.isError ? control.value ? { duplicated: true } : { required: true } : {};
  }

  private patchValues(profile: IShortLineProfile): void {
    this.summary.patchValue(profile);
    this.patchContactsValue(profile);
    this.pathComments(profile);
    this.patchSalesRepresentative(profile);
  }

  private patchContactsValue(profile: IShortLineProfile): void {
    const contact: IShortLineContact = profile.contacts.filter((item: IShortLineContact) => item.type === EShortLineProfile.PRIMARY_CONTACT)[0];
    if (contact) {
      this.summary.patchValue(contact);
      this.summary.controls.primaryContact.patchValue(contact.fullName);
      this.summary.controls.addressCity.patchValue({
        key: contact.addressCity ? contact.addressCity : '',
        value: contact.addressCity ? contact.addressCity : ''
      });

      this.summary.controls.addressState.patchValue({
        key: contact.addressState ? contact.addressState : '',
        value: contact.addressState ? contact.addressState : ''
      });
    }
  }

  private patchSalesRepresentative(profile: IShortLineProfile): void {
    const contact: IShortLineContact = profile.contacts.filter((item: IShortLineContact) => item.type === EShortLineProfile.SALES_REPRESENTATIVE_CONTACT)[0];
    if (contact) {
      this.summary.controls.salesRepresentative.patchValue(contact.fullName);
    } else {
      this.summary.controls.salesRepresentative.patchValue('None Designated');
    }
  }

  private pathComments(profile: IShortLineProfile): void {
    profile.comments.forEach((comment: IShortLineComment) => {
      switch (comment.type) {
        case EShortLineCommentType.COMMERCIAL_NS_COMMENT:
          this.summary.controls.nsComments.patchValue(comment.text);
          break;
        case EShortLineCommentType.COMMERCIAL_HISTORY_COMMENT:
          this.summary.controls.history.patchValue(comment.text);
          break;
        case EShortLineCommentType.COMMERCIAL_EQUIPMENT_OWNERSHIP_COMMENT:
          this.summary.controls.equipmentInfo.patchValue(comment.text);
          break;
        case EShortLineCommentType.COMMERCIAL_PAPER_BARRIERS_COMMENT:
          this.summary.controls.paperBarriers.patchValue(comment.text);
          break;
        case EShortLineCommentType.COMMERCIAL_ACCOUNTING_STATUS_COMMENT:
          this.summary.controls.accountComments.patchValue(comment.text);
          break;
      }
    });
  }

  private reset(): void {
    this.summary.reset();
    this.summary.disable();
    this.isEdit = false;
  }

  private setup(): void {
    this.summary.controls.salesRepresentative.disable();
    this.summary.controls.history.disable();
    this.summary.controls.nsComments.disable();
    if (this.isNew) {
      this.summary.controls.salesRepresentative.setValue('None Designated');
    }
    this.summary.controls.addressState.valueChanges.pipe(
      debounceTime(this.debounceTimeout),
      distinctUntilChanged(),
      takeUntil(this.subscriptionNotifier)
    ).subscribe((state: string | INSAutoCompleteOption) => {
      if (!state || typeof state === 'string') {
        this.summary.controls.addressCity.reset();
        this.summary.controls.addressCity.disable();
      }
    });

    this.summary.controls.addressCity.valueChanges.pipe(
      debounceTime(this.debounceTimeout),
      distinctUntilChanged(),
      takeUntil(this.subscriptionNotifier))
      .subscribe((stationName: string) => {
        if (stationName && stationName.length > 2) {
          this.loadOriginStationsData.emit({
            stateAbbreviation: this.summary.controls.addressState.value.key,
            station: stationName
          });
        }
      });

    this.summary.controls.reportingMark.valueChanges.pipe(
      debounceTime(this.debounceTimeout),
      distinctUntilChanged(),
      takeUntil(this.subscriptionNotifier))
      .subscribe((reportingMark: string) => {
        if (reportingMark && reportingMark.length > 1 && (this.isEdit || this.isNew)) {
          this.searchReportingMark.emit(reportingMark);
        }
      });
  }

  private disableUnusedFields(): void {
    this.summary.controls.nsComments.disable();
    this.summary.controls.history.disable();
    this.summary.controls.salesRepresentative.disable();
    this.summary.controls.salesRepresentative.patchValue('None Designated');
  }

  private getComments(): Array<IShortLineComment> {
    const comments: Array<IShortLineComment> = [];
    if (this.summary.value.nsComments) {
      comments.push({
        text: this.summary.value.nsComments,
        type: EShortLineCommentType.COMMERCIAL_NS_COMMENT
      });
    }

    if (this.summary.value.history) {
      comments.push({
        text: this.summary.value.history,
        type: EShortLineCommentType.COMMERCIAL_HISTORY_COMMENT
      });
    }

    if (this.summary.value.equipmentInfo) {
      comments.push({
        text: this.summary.value.equipmentInfo,
        type: EShortLineCommentType.COMMERCIAL_EQUIPMENT_OWNERSHIP_COMMENT
      });
    }

    if (this.summary.value.paperBarriers) {
      comments.push({
        text: this.summary.value.paperBarriers,
        type: EShortLineCommentType.COMMERCIAL_PAPER_BARRIERS_COMMENT
      });
    }

    if (this.summary.value.accountComments) {
      comments.push({
        text: this.summary.value.accountComments,
        type: EShortLineCommentType.COMMERCIAL_ACCOUNTING_STATUS_COMMENT
      });
    }
    return comments;
  }

  private getContacts(): Array<IShortLineContact> {
    const contacts: Array<IShortLineContact> = [];
    const data = { ...this.summary.value };
    if (this.summary.controls.primaryContact.value) {
      contacts.push(
        {
          addressCity: data.addressCity ? data.addressCity.key : undefined,
          addressState: data.addressState ? data.addressState.key : undefined,
          addressLine1: data.addressLine ? data.addressLine : undefined,
          addressZip: data.addressZip ? data.addressZip : undefined,
          email: data.email ? data.email : undefined,
          fax: data.fax ? data.fax.replace(new RegExp('\\(|\\)|\-', 'g'), '') : undefined,
          fullName: data.primaryContact,
          phone: data.phone ? data.phone.replace(new RegExp('\\(|\\)|\-', 'g'), '') : undefined,
          reportingMark: data.reportingMark,
          title: data.title ? data.title : undefined,
          type: EShortLineProfile.PRIMARY_CONTACT
        }
      );
    }
    if (this.summary.controls.salesRepresentative.value) {
      contacts.push(
        {
          fullName: this.summary.controls.salesRepresentative.value,
          reportingMark: data.reportingMark,
          type: EShortLineProfile.SALES_REPRESENTATIVE_CONTACT
        }
      );
    }
    return contacts;
  }
}
